/**
 * LT-18 §11 bug 12 — normalise every FastAPI error into a human string.
 *
 * FastAPI's HTTPException(422) returns `{"detail": [{loc, msg, type}, ...]}`
 * — an array of objects. Passing that array into React (or into
 * `toast.error(...)` which then renders through Sonner) throws
 * `Objects are not valid as a React child` and white-screens the tree.
 *
 * `formatApiError` collapses every shape we ever see into a plain
 * string, so a component can safely stash the result in state and
 * render it in JSX without a class of runtime surprises.
 *
 * Order of rules (first match wins):
 *   1. string detail                 → returned as-is
 *   2. list-of-Pydantic-errors       → joined `msg` values
 *   3. object detail with `.message` → LT-18c §4 structured 409 shape
 *   4. object detail with `.msg`     → legacy middleware wrap
 *   5. axios-level message           → passed through
 *   6. anything else                 → the fallback string the caller
 *                                       supplied (or a generic default)
 *
 * Never returns null / undefined / an object. Callers can drop the
 * return value straight into JSX.
 *
 * LT-18c §4 addition — `parseApiError(err, fallback)` returns the same
 * plain-string `.message` PLUS any structured extras from a taxonomy /
 * supplier 409 (existing_id, existing_label, existing_status). Used by
 * `ManagedListPanel` to render an inline "Restore this one" CTA when a
 * user tries to re-create the archived twin of an existing row.
 */
export function parseApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return { message: fallback };

  const detail = err?.response?.data?.detail;

  // Rule 1 — plain string detail (the common case).
  if (typeof detail === "string" && detail.trim()) {
    return { message: detail };
  }

  // Rule 2 — Pydantic validation array.
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail
      .map((d) => (typeof d === "string" ? d : d?.msg || ""))
      .filter((s) => s && s.trim());
    if (parts.length > 0) return { message: parts.join("; ") };
  }

  // Rule 3 / 4 — object detail. LT-18c §4 routes emit `.message` +
  // `.existing_id` / `.existing_label` / `.existing_status`; legacy
  // middleware wraps produce `.msg`.
  if (detail && typeof detail === "object") {
    const message =
      (typeof detail.message === "string" && detail.message) ||
      (typeof detail.msg === "string" && detail.msg) ||
      err.message ||
      fallback;
    const out = { message };
    if (detail.existing_id)     out.existing_id     = detail.existing_id;
    if (detail.existing_label)  out.existing_label  = detail.existing_label;
    if (detail.existing_status) out.existing_status = detail.existing_status;
    return out;
  }

  // Rule 5 — axios / network-level message.
  if (typeof err.message === "string" && err.message.trim()) {
    return { message: err.message };
  }

  return { message: fallback };
}

export function formatApiError(err, fallback) {
  return parseApiError(err, fallback).message;
}
